import { Component, OnChanges, Input, ViewChildren, QueryList, OnDestroy, ChangeDetectorRef, AfterViewChecked, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, ValidationErrors, FormControl } from '@angular/forms';
import { DataFieldComponentBase } from './datafield-base-component';
import { DataFieldGroup } from './models/datafield-group';
import { DataFieldTypes } from './models/datafield-type';
import { DataField } from './models/datafield';
import { Guid } from '../utils';
import { NgClass } from '@angular/common';
import { DataFieldTextBoxComponent } from './datafield-textbox.component';
import { DataFieldCheckBoxComponent } from './datafield-checkbox.component';
import { DataFieldDropDownComponent } from './datafield-dropdown.component';
import { DataFieldPasswordComponent } from './datafield-password.component';
import { DataFieldFileUploadComponent } from './datafield-file-upload.component';
import { DataFieldArtifactXmlPickerComponent } from './datafield-artifact-xml-picker.component';
import { DataFieldArtifactAssemblyPickerComponent } from './datafield-artifact-assembly-picker.component';
import { DataFieldArrayComponent } from './datafield-array.component';
import { DatafieldXpathComponent } from './datafield-xpath.component';

@Component({
    selector: 'ep-datafield-group-container',
    templateUrl: './datafield-group-container.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgClass, DataFieldTextBoxComponent, DataFieldCheckBoxComponent, DataFieldDropDownComponent, DataFieldPasswordComponent, DataFieldFileUploadComponent, DataFieldArtifactXmlPickerComponent, DataFieldArtifactAssemblyPickerComponent, DataFieldArrayComponent, DatafieldXpathComponent]
})

export class DataFieldGroupContainerComponent implements OnChanges, OnDestroy, AfterViewChecked {

    @Input() numberOfColumns: number = 1;
    @Input() datafieldGroups: DataFieldGroup[];
    @Input() isAlwaysEditable: boolean;
    @Input() showGroupHeaders: boolean = true;
    @Input() formValidationGroup: UntypedFormGroup;
    @Input() showError: boolean;
    @Input() referenceId: number;            // Optional value for the container to hold an id of what it is referencing.

    @Output() changes = new EventEmitter<void>();

    private formSub?: { unsubscribe(): void };

    @ViewChildren('datafieldComponent')
    public allDataFieldComponents: QueryList<DataFieldComponentBase>;
    public currentColumnClass: string;
    private validationControlNames: string[] = [];

    constructor(private ref: ChangeDetectorRef) {

    }

    ngOnChanges(changes: any) {
        let runAgain = true;
        if ((changes.datafieldGroups || changes.numberOfColumns) && this.datafieldGroups) {
            if (12 % this.numberOfColumns != 0) {
                throw Error('numberOfColumns must be divisible by 12');
            }

            this.datafieldGroups = this.datafieldGroups.sort((dg1, dg2) => dg1.SortOrder - dg2.SortOrder);

            this.currentColumnClass = "col-sm-" + (12 / this.numberOfColumns);
            this.datafieldGroups.forEach((group) => {

                group.DataFields = group.DataFields.sort((df1: DataField, df2: DataField) => df1.SortOrder - df2.SortOrder);

                group.DataFields.forEach(df => df.UniqueId = Guid.newGuid()); // Make sure that all datafields have a unique identification - because validation will cause troubles if multiple identical datafields are shown.

                // Move this chunk-code to a utility service
                let chunkedRows: DataField[][] = [];
                for (var i = 0; i < group.DataFields.length; i += this.numberOfColumns) {
                    let datafields = group.DataFields.slice(i, i + this.numberOfColumns);
                    chunkedRows.push(datafields);
                }
                group['chunkedRows'] = chunkedRows;
            });
            this.setValidators();
            this.checkDependsOnFieldsInitial();
            runAgain = false;
        }

        if (changes.formValidationGroup && runAgain) {
            this.setValidators();
            this.checkDependsOnFieldsInitial();
        }
    }

    ngAfterViewChecked(): void {
        this.updateValueAndValidity();
        this.ref.detectChanges();
    }

    ngOnDestroy(): void {
        this.formSub?.unsubscribe();
        this.clearValidators(); // Make sure to remove the dynamic controls from the form-group if this component is disposed.
    }

    private updateValueAndValidity() {
        if (this.formValidationGroup && this.formValidationGroup.enabled) {
            this.formValidationGroup.updateValueAndValidity();
        }
    }

    private clearValidators() {
        if (this.formValidationGroup) {
            // Because unique identifiers are created for each control, we need to remove the validation if it has already been set. Otherwise the "old" validators will remain in the form and have effect on the valid/invalid state.

            // If the last control in the formgroup is removed and it was disabled then the form is automatically disabled as well.
            // To prevent this we are now checking if the last control to be removed is disabled and if it is, then we add a dummy control and remove both. This we way ensure the final control removed is enabled.
            // This 'hack' was the least invasive way of ensuring the form was not disabled. For example, manipulating the form's state directly would cause stateChanged events to be emitted and the consequences of that are difficult to predict.
            for (var i = 0; i < this.validationControlNames.length; i++ ) {
                const validationControlNameToRemove = this.validationControlNames[i];
                if (i == this.validationControlNames.length - 1 && this.formValidationGroup.controls[validationControlNameToRemove]?.disabled) {
                    const dummyControl = new FormControl();
                    const dummyControlName = 'DummyControlToBeRemoved';
                    this.formValidationGroup.addControl(dummyControlName, dummyControl);
                    this.formValidationGroup.removeControl(validationControlNameToRemove);
                    this.formValidationGroup.removeControl(dummyControlName);
                } else {
                    this.formValidationGroup.removeControl(validationControlNameToRemove);
                }
            }

        }
    }

    checkDependsOnFieldsInitial() {
        if (this.datafieldGroups) {
            this.datafieldGroups.forEach(group => {
                group.DataFields.forEach(datafield => {
                    this.checkDependsOnFields(datafield);
                });
            });
        }
    }

    checkDependsOnFields(changedDatafield: DataField) {
        // Only check depends-on if it's a checkbox or dropdown...
        if (this.datafieldGroups && (changedDatafield.DataType.DataTypeEnum == DataFieldTypes.CheckBox || changedDatafield.DataType.DataTypeEnum == DataFieldTypes.DropDown)) {
            this.datafieldGroups.forEach(group => {
                group.DataFields.forEach(datafield => {
                    if (datafield.DependsOnCallbackReferenceId == changedDatafield.CallbackReferenceId && datafield.DependsOnValues) {
                        const isHidden = changedDatafield.IsHidden || !datafield.DependsOnValues.some(v => changedDatafield.Value?.toString().toLocaleLowerCase() == v?.toLocaleLowerCase());
                        const isChanged = isHidden !== datafield.IsHidden;
                        datafield.IsHidden = isHidden;

                        if (datafield.IsHidden) {
                            this.formValidationGroup.controls[DataFieldComponentBase.generateIdentifier(datafield)].disable();
                        }
                        else {
                            this.formValidationGroup.controls[DataFieldComponentBase.generateIdentifier(datafield)].enable();
                        }

                        if (isChanged) {
                            // Recursive call - if current data-field has been hidden (or un-hidden), then fields depending on it will also need to be hidden (or un-hidden)
                            this.checkDependsOnFields(datafield);
                        }
                    }
                });

                group['hidden'] = group.DataFields.every(df => df.IsHidden);
            });
        }

        // To avoid the 'expression has changed' exception...
        this.updateValueAndValidity();
        this.ref.detectChanges();
    }

    private setValidators() {
        this.clearValidators();

        let validationGroup = {};
        if (this.datafieldGroups) {
            this.datafieldGroups.forEach(group => {
                group.DataFields.forEach(datafield => {
                    let validationIdentifier = DataFieldComponentBase.generateIdentifier(datafield);
                    this.validationControlNames.push(validationIdentifier);
                    // Ignore checkboxes - required doens't make sense here
                    // TODO: Implement validation for type 'File'
                    if (datafield.IsHidden || datafield.DataType.DataTypeEnum == DataFieldTypes.CheckBox || datafield.DataType.DataTypeEnum == DataFieldTypes.File) {
                        validationGroup[validationIdentifier] = new UntypedFormControl(datafield.Value?.toString() ?? '');
                    }
                    else if (datafield.DataType.DataTypeEnum == DataFieldTypes.Password) {
                        DataField.RemapSecret(datafield);

                        let pwdValidator = (control: UntypedFormControl) => DataFieldGroupContainerComponent.validatePassword(datafield, control);
                        validationGroup[validationIdentifier] = datafield.IsRequired ? new UntypedFormControl(datafield.Value || '', pwdValidator) : new UntypedFormControl(datafield.Value || '');
                    }
                    else if (datafield.DataType.DataTypeEnum == DataFieldTypes.AssemblyArtifact || datafield.DataType.DataTypeEnum == DataFieldTypes.XmlArtifact) {
                        let artifactValidator = (control: UntypedFormControl) => DataFieldGroupContainerComponent.validateArtifact(datafield, control);
                        validationGroup[validationIdentifier] = datafield.IsRequired ? new UntypedFormControl(datafield.Value || '', artifactValidator) : new UntypedFormControl(datafield.Value || '');
                    }
                    else {
                        let validators = [];
                        // Skipping required validation for array data fields since this is handled by Min/Max properties on the array definition
                        if (datafield.IsRequired && datafield.DataType.DataTypeEnum !== DataFieldTypes.Array) validators.push(Validators.required);
                        if (datafield.RegEx) validators.push(Validators.pattern(datafield.RegEx));
                        validationGroup[validationIdentifier] = new UntypedFormControl(datafield.Value?.toString() ?? '', validators);
                    }
                });
            });
        }
        this.formValidationGroup = this.formValidationGroup || new UntypedFormGroup(validationGroup); // Create a new instance if no FormGroup is set from the parent component

        Object.assign(this.formValidationGroup.controls, validationGroup);
        this.updateValueAndValidity();

        // (re)subscribe to changes
        this.formSub?.unsubscribe();
        this.formSub = this.formValidationGroup.valueChanges.subscribe(() => {
            // emit a simple signal; parent can recompute counts
            this.changes.emit();
        });
    }

    private static validateArtifact(datafield: DataField, control: UntypedFormControl): ValidationErrors | null {
        if (!datafield.Value || datafield.Value == Guid.empty) {
            return {
                required: {
                    valid: false
                }
            };
        }

        return null;
    }

    private static validatePassword(datafield: DataField, control: UntypedFormControl): ValidationErrors | null {
        return ((control && control.value) || (datafield && datafield.SecretId)) ? null : {
            required: {
                valid: false
            }
        };
    }

    /**
     * Gets the datafields in all groups
     */
    public getDataFields(): DataField[] {
        return DataFieldGroup.getDataFields(this.datafieldGroups);
    }

    public getDataFieldValues(includeEmptyFields: boolean) {
        return DataFieldGroup.getDataFieldValues(this.datafieldGroups, includeEmptyFields);
    }

    public edit() {
        this.allDataFieldComponents.forEach((g) => g.edit());
    }

    public commit() {
        this.allDataFieldComponents.forEach((g) => g.commit());
    }

    public cancel() {
        this.allDataFieldComponents.forEach((g) => g.cancel());
    }
}
